import { ItemGrid } from '@chemist4u/newt-components';
import ProductPreview from '@modules/products/components/product-preview';
import type { ProductCardVM } from 'types/view-models';

type ProductGridProps = {
  products: ProductCardVM[];
  /** Product IDs to force to the front of the grid. Only takes effect on
   * page 1 — pinning further into a paginated result would move a product
   * off the page it's actually ranked into. No real pinning data source
   * exists yet (see PLP pagination plan, Faza 4) — always `[]` today. */
  pinnedProductIds?: string[];
  page: number;
};

function withPinnedFirst(
  products: ProductCardVM[],
  pinnedProductIds: string[],
  page: number,
): ProductCardVM[] {
  if (page !== 1 || pinnedProductIds.length === 0) return products;

  const pinned: ProductCardVM[] = [];
  const rest: ProductCardVM[] = [];
  for (const product of products) {
    if (pinnedProductIds.includes(product.id)) {
      pinned.push(product);
    } else {
      rest.push(product);
    }
  }
  // Preserve the pinned list's own order, not the grid's original order.
  pinned.sort(
    (a, b) => pinnedProductIds.indexOf(a.id) - pinnedProductIds.indexOf(b.id),
  );
  return [...pinned, ...rest];
}

/** Renders exactly one page of products — no infinite-scroll sentinel. */
export default function ProductGrid({
  products,
  pinnedProductIds = [],
  page,
}: ProductGridProps) {
  const ordered = withPinnedFirst(products, pinnedProductIds, page);

  return (
    <div data-testid="products-list">
      <ItemGrid itemSize="default">
        {ordered.map((card) => (
          <ProductPreview key={card.id} card={card} />
        ))}
      </ItemGrid>
    </div>
  );
}
